import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  useStoreSupplierPaymentMutation,
  useUpdateCashReceiptMutation,
  useReprintCashReceiptMutation,
} from "../../../../store/queries/Delivery";
import { useGetOrdersBasicDataQuery } from "../../../../store/queries/Orders/index.js";
import { updateConfig } from "../../../../store/slices/Delivery/invoicesSlice";

const useSupplierPaymentForm = ({
  onClose,
  onPrintReady,
  mode = "create",
  editData = null,
}) => {
  const isEdit = mode === "edit";
  const dispatch = useDispatch();
  const { selectedSupplier } = useSelector((state) => state.deliveryInvoices);

  const { data: basicData = {}, isLoading: isBasicDataLoading } =
    useGetOrdersBasicDataQuery();

  const [storeSupplierPayment, { isLoading: isStoring }] =
    useStoreSupplierPaymentMutation();
  const [updateCashReceipt, { isLoading: isUpdating }] =
    useUpdateCashReceiptMutation();
  const [reprintCashReceipt] = useReprintCashReceiptMutation();

  const isSubmitting = isStoring || isUpdating;

  const shopOptions = useMemo(
    () => basicData?.data?.supplier_list ?? [],
    [basicData],
  );

  const paymentOptions = useMemo(
    () => basicData?.data?.payment_mode ?? [],
    [basicData],
  );

  const supplierId = isEdit
    ? editData?.supplier?.id ?? editData?.supplier_id ?? ""
    : selectedSupplier?._id ?? "";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      supplier_id: supplierId,
      payment_method: isEdit ? editData?.payment_method ?? "" : "",
      received_amount: isEdit ? editData?.received_amount ?? "" : "",
      remark: isEdit ? editData?.remark ?? "" : "",
    },
    validate: (values) => {
      const errors = {};
      if (!values.supplier_id) errors.supplier_id = "Required";
      if (!values.payment_method) errors.payment_method = "Required";
      const amount = Number(values.received_amount);
      if (values.received_amount === "" || Number.isNaN(amount) || amount < 0) {
        errors.received_amount = "Enter a valid amount";
      }
      return errors;
    },
    onSubmit: async (values) => {
      try {
        let response;
        if (isEdit) {
          response = await updateCashReceipt({
            cash_receipt_id: editData?.cash_receipt_id ?? editData?._id,
            supplier_id: values.supplier_id,
            payment_method: values.payment_method,
            received_amount: Number(values.received_amount),
            remark: values.remark,
          }).unwrap();
        } else {
          response = await storeSupplierPayment({
            supplier_id: values.supplier_id,
            payment_method: values.payment_method,
            received_amount: Number(values.received_amount),
            remark: values.remark,
          }).unwrap();
        }

        if (response?.status_code === 200 || response?.success) {
          let printData = response?.data ?? response;
          if (isEdit && !printData?.receipt_number) {
            const reprintRes = await reprintCashReceipt({
              cash_receipt_id: editData?.cash_receipt_id ?? editData?._id,
            }).unwrap();
            printData = reprintRes?.data ?? reprintRes;
          }
          dispatch(
            updateConfig((state) => {
              state.receiptPrintData = printData;
            }),
          );
          toast.success(
            response?.message ||
              (isEdit ? "Supplier payment updated" : "Supplier payment created"),
          );
          onClose?.();
          onPrintReady?.();
        } else {
          toast.error(
            response?.message ||
              (isEdit
                ? "Failed to update supplier payment"
                : "Failed to create supplier payment"),
          );
        }
      } catch (err) {
        toast.error(
          err?.data?.message ||
            (isEdit
              ? "Failed to update supplier payment"
              : "Failed to create supplier payment"),
        );
      }
    },
  });

  const getFieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? formik.errors[name] : "";

  return {
    formik,
    shopOptions,
    paymentOptions,
    isBasicDataLoading,
    isSubmitting,
    getFieldError,
  };
};

export default useSupplierPaymentForm;
